import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { getMovements, getMasters, deleteMovement, MOVEMENT_TYPES, DELIVERY_MODES, errorMessage } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { LoadError } from '../components/LoadError';
import { TruncationNotice, totalFromHeaders } from '../components/TruncationNotice';
import { 
    Plus, 
    Truck, 
    Trash2,
    Clock,
    Package,
    HardDrive
} from 'lucide-react';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function Movements() {
    const [movements, setMovements] = useState([]);
    const [masters, setMasters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(null);
    const [total, setTotal] = useState(null);
    const [typeFilter, setTypeFilter] = useState('all');
    const [masterFilter, setMasterFilter] = useState('all');
    const [deleteId, setDeleteId] = useState(null);
    const { isManager, isAdmin } = useAuth();
    const navigate = useNavigate();

    useEffect(() => {
        fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reload only when these inputs change; the loader is recreated each render and reads current state
    }, [typeFilter, masterFilter]);

    const fetchData = async () => {
        try {
            setLoadError(null);
            const [movementsRes, mastersRes] = await Promise.all([
                getMovements({
                    type: typeFilter !== 'all' ? typeFilter : undefined,
                    master_id: masterFilter !== 'all' ? masterFilter : undefined
                }),
                getMasters()
            ]);
            setMovements(movementsRes.data);
            setTotal(totalFromHeaders(movementsRes));
            setMasters(mastersRes.data);
        } catch (error) {
            if (!error._isAuthError) setLoadError(errorMessage(error, 'Erreur lors du chargement des mouvements'));
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteId) return;
        try {
            await deleteMovement(deleteId);
            toast.success('Mouvement supprimé');
            setDeleteId(null);
            fetchData();
        } catch (error) {
            toast.error(errorMessage(error, 'Erreur lors de la suppression'));
        }
    };

    const getMasterName = (masterId) => {
        const master = masters.find(m => m.id === masterId);
        return master?.name || 'N/A';
    };

    const getTypeColor = (type) => {
        switch (type) {
            case 'reception': return 'status-en_stock';
            case 'expedition': return 'status-chez_client';
            case 'transfert': return 'status-ecrase';
            default: return '';
        }
    };

    return (
        <Layout>
            <div className="p-4 sm:p-6 space-y-6 animate-fade-in" data-testid="movements-page">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className="font-heading text-3xl font-bold tracking-tight">
                            MOUVEMENTS
                        </h1>
                        <p className="text-muted-foreground mt-1">
                            Suivi des entrées et sorties
                        </p>
                    </div>
                    {isManager() && (
                        <Button onClick={() => navigate('/movements/new')} data-testid="new-movement-button">
                            <Plus className="w-4 h-4 mr-2" />
                            Nouveau mouvement
                        </Button>
                    )}
                </div>

                {/* Filters */}
                <div className="flex flex-col sm:flex-row gap-4">
                    <Select value={typeFilter} onValueChange={setTypeFilter}>
                        <SelectTrigger className="w-full sm:w-[180px]" data-testid="type-filter">
                            <SelectValue placeholder="Tous les types" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les types</SelectItem>
                            {Object.entries(MOVEMENT_TYPES).map(([key, label]) => (
                                <SelectItem key={key} value={key}>{label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                    <Select value={masterFilter} onValueChange={setMasterFilter}>
                        <SelectTrigger className="w-full sm:w-[300px]" data-testid="master-filter">
                            <SelectValue placeholder="Tous les masters" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="all">Tous les masters</SelectItem>
                            {masters.map(master => (
                                <SelectItem key={master.id} value={master.id}>
                                    <span className="font-mono text-xs">{master.name.substring(0, 40)}...</span>
                                </SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>

                {!loading && !loadError && <TruncationNotice shown={movements.length} total={total} noun="mouvements" />}
                {/* Movements Table */}
                {loading ? (
                    <Skeleton className="h-96" />
                ) : loadError ? (
                    <LoadError message={loadError} onRetry={() => { setLoading(true); fetchData(); }} />
                ) : movements.length === 0 ? (
                    <Card className="bg-card border-border">
                        <CardContent className="flex flex-col items-center justify-center py-16">
                            <Truck className="w-16 h-16 text-muted-foreground/50 mb-4" />
                            <h3 className="font-heading text-xl font-semibold mb-2">
                                Aucun mouvement
                            </h3>
                            <p className="text-muted-foreground text-center max-w-md mb-6">
                                {typeFilter !== 'all' || masterFilter !== 'all'
                                    ? 'Aucun mouvement ne correspond à vos filtres.'
                                    : 'Enregistrez votre premier mouvement de stock.'}
                            </p>
                            {isManager() && (
                                <Button onClick={() => navigate('/movements/new')}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Créer un mouvement
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                ) : (
                    <>
                    <ul className="sm:hidden space-y-2" aria-label="Mouvements">
                        {movements.map((movement) => (
                            <li
                                key={movement.id}
                                className="relative p-3 rounded-lg border border-border bg-card hover:border-primary/30 transition-colors focus-within:ring-1 focus-within:ring-ring"
                                data-testid={`movement-card-${movement.id}`}
                            >
                                <div className="flex items-start gap-2">
                                    <div className="flex-1 min-w-0">
                                        <div className="flex flex-wrap items-center gap-2">
                                            <Badge variant="outline" className={`text-xs ${getTypeColor(movement.type)}`}>
                                                {MOVEMENT_TYPES[movement.type] || movement.type}
                                            </Badge>
                                            <Link
                                                to={`/movements/${movement.id}`}
                                                className="text-sm after:absolute after:inset-0 after:content-[''] focus-visible:outline-none"
                                            >
                                                {format(new Date(movement.date), 'dd MMM yyyy', { locale: fr })}
                                            </Link>
                                        </div>
                                        <p className="mt-2 font-mono text-sm break-all">{getMasterName(movement.master_id)}</p>
                                        {(movement.delivery_mode || movement.tracking_number) && (
                                            <p className="mt-1 text-xs text-muted-foreground">
                                                {movement.delivery_mode ? DELIVERY_MODES[movement.delivery_mode] : ''}
                                                {movement.delivery_mode && movement.tracking_number ? ' · ' : ''}
                                                {movement.tracking_number && <span className="font-mono">{movement.tracking_number}</span>}
                                            </p>
                                        )}
                                    </div>
                                    {isAdmin() && (
                                        <Button
                                            variant="ghost"
                                            size="icon" aria-label="Supprimer ce mouvement"
                                            className="relative z-10 -mr-1 -mt-1 h-8 w-8 text-muted-foreground hover:text-destructive-text"
                                            onClick={() => setDeleteId(movement.id)}
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </Button>
                                    )}
                                </div>
                            </li>
                        ))}
                    </ul>
                    <Card className="hidden sm:block bg-card border-border overflow-hidden">
                        <div className="overflow-x-auto">
                            <Table>
                                <TableHeader>
                                    <TableRow className="bg-muted/50 hover:bg-muted/50">
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Date</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Type</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Master</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Mode</TableHead>
                                        <TableHead className="font-bold text-xs uppercase tracking-wider">Suivi</TableHead>
                                        {isAdmin() && (
                                            <TableHead className="font-bold text-xs uppercase tracking-wider w-[80px]">Actions</TableHead>
                                        )}
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {movements.map((movement) => (
                                        <TableRow 
                                            key={movement.id} 
                                            className="hover:bg-muted/30 cursor-pointer"
                                            data-testid={`movement-row-${movement.id}`}
                                            onClick={() => navigate(`/movements/${movement.id}`)}
                                        >
                                            <TableCell>
                                                <div className="flex items-center gap-2 text-sm">
                                                    <Clock className="w-4 h-4 text-muted-foreground" aria-hidden="true" />
                                                    <Link
                                                        to={`/movements/${movement.id}`}
                                                        onClick={(e) => e.stopPropagation()}
                                                        className="hover:text-primary focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                                                    >
                                                        {format(new Date(movement.date), 'dd MMM yyyy', { locale: fr })}
                                                    </Link>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <Badge variant="outline" className={`text-xs ${getTypeColor(movement.type)}`}>
                                                    {MOVEMENT_TYPES[movement.type] || movement.type}
                                                </Badge>
                                            </TableCell>
                                            <TableCell>
                                                <Link 
                                                    to={`/masters/${movement.master_id}`}
                                                    className="flex items-center gap-1 text-sm font-mono hover:text-primary transition-colors max-w-[300px] focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring rounded-sm"
                                                    title={getMasterName(movement.master_id)}
                                                >
                                                    <HardDrive className="w-3 h-3 flex-shrink-0" aria-hidden="true" />
                                                    <span className="truncate">{getMasterName(movement.master_id)}</span>
                                                </Link>
                                            </TableCell>
                                            <TableCell className="text-sm">
                                                {movement.delivery_mode ? DELIVERY_MODES[movement.delivery_mode] : '-'}
                                            </TableCell>
                                            <TableCell className="font-mono text-xs">
                                                {movement.tracking_number || '-'}
                                            </TableCell>
                                            {isAdmin() && (
                                                <TableCell>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon" aria-label="Supprimer ce mouvement"
                                                        className="h-8 w-8 text-muted-foreground hover:text-destructive-text"
                                                        onClick={(e) => { e.stopPropagation(); setDeleteId(movement.id); }}
                                                    >
                                                        <Trash2 className="w-4 h-4" />
                                                    </Button>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    </Card>
                    </>
                )}
            </div>

            {/* Delete Dialog */}
            <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>Supprimer ce mouvement ?</AlertDialogTitle>
                        <AlertDialogDescription>
                            Cette action est irréversible.
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>Annuler</AlertDialogCancel>
                        <AlertDialogAction onClick={handleDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
                            Supprimer
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </Layout>
    );
}
